import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { errorMessage } from '../services/apiError';
import { projectService, type Project } from '../services/projectService';

import { ActionBarActions } from './AiOverviewPanel/ActionBarActions';
import { MessageIdentityLabel } from './AiOverviewPanel/MessageIdentityLabel';

import './ProjectTitleConfirmation.css';

interface ProjectTitleConfirmationOptions {
  requestKey: string | null;
  currentTitle: string | null;
  projectId: string | null;
  sessionId?: string | null;
  onConfirmed: (project: Project) => void | Promise<void>;
  onRefreshActions?: () => void;
}

type AvailabilityState = {
  available: boolean | null;
  checking: boolean;
  message: string;
};

const MIN_TITLE_LENGTH = 2;
const MAX_TITLE_LENGTH = 54;

export const normalizeProjectTitle = (title: string): string => title
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, MAX_TITLE_LENGTH)
  .replace(/-+$/g, '');

export const normalizeTitleInput = (title: string): string => title
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+/g, '')
  .slice(0, MAX_TITLE_LENGTH);

const getErrorMessage = (error: unknown, titleSaved: boolean): string => errorMessage(
  error,
  titleSaved
    ? 'Your title is saved, but planning could not start. Select Save & Start to retry.'
    : 'We couldn’t confirm the project title. Try saving again.',
);

const validateTitle = (title: string): string | null => {
  const cleaned = normalizeProjectTitle(title);

  if (cleaned.length < MIN_TITLE_LENGTH) return 'Title must be at least 2 characters.';
  if (cleaned.length > MAX_TITLE_LENGTH) return 'Title must be 54 characters or fewer.';
  for (let i = 0; i < cleaned.length; i++) {
    if (cleaned.charCodeAt(i) < 32) {
      return 'Title cannot contain control characters.';
    }
  }

  return null;
};

export const useTitleAvailability = (title: string, projectId: string | null, currentTitle: string) => {
  const [result, setResult] = useState<(AvailabilityState & { title: string; projectId: string }) | null>(null);
  const validationError = validateTitle(title);
  const titleIsCurrent = normalizeProjectTitle(title) === currentTitle;

  useEffect(() => {
    if (!projectId || validationError || titleIsCurrent) return undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      projectService.checkTitleAvailability(normalizeProjectTitle(title), projectId)
        .then(({ available }) => {
          if (!active) return;
          setResult({
            title, projectId, available, checking: false,
            message: available ? 'Available' : 'Already taken. Try a different title.',
          });
        })
        .catch(() => {
          if (!active) return;
          setResult({
            title, projectId, available: null, checking: false,
            message: 'Could not check availability. You can still try saving.',
          });
        });
    }, 400);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [projectId, title, titleIsCurrent, validationError]);

  if (validationError) return { available: false, checking: false, message: validationError, validationError };
  if (titleIsCurrent) return { available: true, checking: false, message: '', validationError };

  return {
    ...(result?.title === title && result.projectId === projectId
      ? result : { available: null, checking: true, message: 'Checking availability...' }),
    validationError,
  };
};

const useTitleDraft = (identity: string | null, currentTitle: string) => {
  const initial = { identity, title: currentTitle, error: '', stage: null as 'saving' | 'starting' | null };
  const [state, setState] = useState(initial);

  if (state.identity !== identity) setState(initial);

  return { state: state.identity === identity ? state : initial, setState };
};

const useActiveConfirmation = (identity: string | null) => {
  const active = useRef({ identity, pending: false });

  useLayoutEffect(() => {
    active.current = { identity, pending: false };

    return () => { active.current = { identity: null, pending: false }; };
  }, [identity]);

  return {
    begin: () => {
      if (active.current.identity !== identity || active.current.pending) return null;
      active.current.pending = true;

      return active.current;
    },
    isCurrent: (attempt: typeof active.current) => active.current === attempt,
    release: (attempt: typeof active.current) => { attempt.pending = false; },
  };
};

// The provider owns this controller so switching chat surfaces preserves the draft and submission lock.
// eslint-disable-next-line max-statements
export const useProjectTitleConfirmation = ({
  requestKey, currentTitle, projectId, sessionId, onConfirmed, onRefreshActions,
}: ProjectTitleConfirmationOptions) => {
  const safeCurrentTitle = normalizeProjectTitle(currentTitle || '') || 'untitled-project';
  const identity = requestKey && projectId ? `${projectId}:${sessionId ?? ''}:${requestKey}` : null;
  const { state, setState } = useTitleDraft(identity, safeCurrentTitle);
  const active = useActiveConfirmation(identity);
  const availability = useTitleAvailability(state.title, identity ? projectId : null, safeCurrentTitle);
  const submitting = state.stage !== null;
  const canSave = !!identity && !availability.validationError && !availability.checking
    && availability.available !== false && !submitting;
  const changeTitle = (value: string) => {
    setState({ ...state, title: normalizeTitleInput(value), error: '' });
  };

  // eslint-disable-next-line max-statements
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave || !projectId || onRefreshActions) return;
    const submission = active.begin();

    if (!submission) return;
    setState({ ...state, stage: 'saving', error: '' });
    let titleSaved = false;

    try {
      const updatedProject = await projectService.confirmTitle(
        projectId, normalizeProjectTitle(state.title), sessionId,
      );

      titleSaved = true;
      if (active.isCurrent(submission)) {
        setState({ ...state, stage: 'starting', error: '' });
        await onConfirmed(updatedProject);
      }
    } catch (err) {
      if (!active.isCurrent(submission)) return;
      setState({ ...state, error: getErrorMessage(err, titleSaved), stage: null });
      active.release(submission);
    }
  };

  return { ...state, submitting, changeTitle, availability, canSave, submit, onRefreshActions };
};

export type ProjectTitleConfirmationController = ReturnType<typeof useProjectTitleConfirmation>;

// eslint-disable-next-line max-lines-per-function
export const ProjectTitleConfirmation: React.FC<{ controller: ProjectTitleConfirmationController }> = ({
  controller: form,
}) => {
  const id = useId();
  const formId = `${id}-form`;
  const submitLabel = form.stage === 'starting' ? 'Starting...' : form.submitting ? 'Saving...' : 'Save & Start';

  return (
    <>
      <section className="ai-overview-bubble ai-overview-action-entry project-title-confirmation">
        <MessageIdentityLabel
          className="ai-overview-bubble__label"
          interactive={false}
          name="Tech Lead Agent"
        />
        <form
          id={formId}
          className="ai-overview-action-entry__body"
          aria-labelledby={`${id}-title`}
          aria-busy={form.submitting}
          onSubmit={form.submit}
        >
          <div className="project-title-confirmation__header">
            <h2 id={`${id}-title`}>Confirm your project title</h2>
            <p>
              This lowercase name identifies your project in planning and in its public product URL.
            </p>
          </div>

          <label className="project-title-confirmation__field">
            <span>Project title</span>
            <input
              value={form.title}
              maxLength={MAX_TITLE_LENGTH}
              onChange={(event) => form.changeTitle(event.target.value)}
              aria-describedby={form.availability.message ? `${id}-availability` : undefined}
              aria-invalid={!!form.availability.validationError || form.availability.available === false}
              disabled={form.submitting || !!form.onRefreshActions}
              autoFocus
            />
          </label>

          {form.availability.message && (
            <div
              id={`${id}-availability`}
              role="status"
              className={`project-title-confirmation__availability${
                form.availability.available === false ? ' project-title-confirmation__availability--error' : ''
              }`}
            >
              {form.availability.message}
            </div>
          )}

          {form.error && <div className="project-title-confirmation__error" role="alert">{form.error}</div>}
        </form>
      </section>
      <div
        className="ai-overview-action-bar ai-overview-action-bar--awaiting-user notranslate"
        translate="no"
        data-action-mirror-owner="current"
        data-action-inline-tail="true"
        data-action-owner-label="Project title confirmation"
        data-awaiting-user="true"
      >
        <ActionBarActions
          attentionActive
          className=" ai-overview-action-bar__actions--count-1"
        >
          <button
            className="ai-overview-action-btn ai-overview-action-btn--run"
            type={form.onRefreshActions ? 'button' : 'submit'}
            form={form.onRefreshActions ? undefined : formId}
            onClick={form.onRefreshActions}
            disabled={form.onRefreshActions ? form.submitting : !form.canSave}
            aria-busy={form.submitting}
            data-attention-primary="true"
          >
            {form.submitting && <span className="ai-overview-action-spinner" aria-hidden="true" />}
            <span className="ai-overview-action-btn__label">
              {form.onRefreshActions ? 'Refresh actions' : submitLabel}
            </span>
          </button>
        </ActionBarActions>
      </div>
    </>
  );
};
